import React from 'react';
import PropTypes from 'prop-types';
import AccountStarIcon from 'mdi-react/AccountStarIcon';
import WebIcon from 'mdi-react/WebIcon';
import CardMembershipIcon from 'mdi-react/CardMembershipIcon';
import NewspaperIcon from 'mdi-react/NewspaperIcon';
import BullhornOutlineIcon from 'mdi-react/BullhornOutlineIcon';
import CalendarOutlineIcon from 'mdi-react/CalendarOutlineIcon';
import TagOutlineIcon from 'mdi-react/TagOutlineIcon';
import ClipboardTextOutlineIcon from 'mdi-react/ClipboardTextOutlineIcon';
import GavelIcon from 'mdi-react/GavelIcon';
import ForumOutlineIcon from 'mdi-react/ForumOutlineIcon';
import BellRingOutlineIcon from 'mdi-react/BellRingOutlineIcon';
import DatabaseImportIcon from 'mdi-react/DatabaseImportIcon';
import ClipboardAccountOutlineIcon from 'mdi-react/ClipboardAccountOutlineIcon';
import CreditCardIcon from 'mdi-react/CreditCardIcon';
import { partial } from '../../../util/translation/translation';
import FeatureShowcase from '../../../shared/components/custom/FeatureShowcase';
import useConfig from '../../../util/useConfig';
import exampleCurrency from './exampleCurrency';
import TierUpgradeFlow from './TierUpgradeFlow';
import TierWebCheckoutPreview from './TierWebCheckoutPreview';

// Everything a tier can open up (each has tier access in its form; pushes
// can be sent to a tier)
const TIER_PRODUCTS = [
  { key: 'news', icon: NewspaperIcon },
  { key: 'spotlight', icon: BullhornOutlineIcon },
  { key: 'events', icon: CalendarOutlineIcon },
  { key: 'offers', icon: TagOutlineIcon },
  { key: 'questionnaires', icon: ClipboardTextOutlineIcon },
  { key: 'auctions', icon: GavelIcon },
  { key: 'chats', icon: ForumOutlineIcon },
  { key: 'push', icon: BellRingOutlineIcon },
];

/**
 * User tiers in two steps: sell them on the club website (collecting member
 * data at checkout), then reward members with a card and exclusive content.
 * Generic examples (Gold, upgrading to Platinum), not the club's tiers.
 */
const TiersShowcase = ({ collapsible, action }) => {
  const s = partial('shared');
  // Generic examples (not the club's tiers): Gold, upgrading to Platinum
  const tierName = s('tiersShowcaseExampleTier');
  const currency = exampleCurrency(useConfig());
  const upgradeName = s('tierFlowExampleUpgrade');
  return (
    <FeatureShowcase
      storageKey="tiersShowcaseHidden"
      icon={AccountStarIcon}
      title={s('tiersInfoTitle')}
      subtitle={s('tiersShowcaseSubtitle')}
      showLabel={s('tiersShowcaseShow')}
      uses={[
        {
          key: 'sell',
          step: s('showcaseStep', { step: 1 }),
          icon: WebIcon,
          label: s('tiersSellLabel'),
          text: s('tiersSellText'),
          cards: [
            {
              key: 'pricing',
              icon: TagOutlineIcon,
              title: s('tiersCardPricing'),
              text: s('tiersCardPricingText'),
              points: [
                {
                  label: s('tiersPriceSubscription'),
                  text: s('tiersPriceSubscriptionText', { currency }),
                },
                {
                  label: s('tiersPriceOneTime'),
                  text: s('tiersPriceOneTimeText', { currency }),
                },
              ],
            },
            {
              key: 'checkout',
              icon: ClipboardAccountOutlineIcon,
              title: s('tiersCardCheckout'),
              text: s('tiersCardCheckoutText'),
            },
            {
              key: 'payment',
              icon: CreditCardIcon,
              title: s('tiersCardPayment'),
              text: s('tiersCardPaymentText'),
              // How members pay, then who handles it
              badges: [s('tiersBadgeCard'), 'Apple Pay', 'Stripe'],
            },
          ],
          // The tier's page on the club website, if they want to see it
          more: {
            showLabel: s('tierWebShow'),
            hideLabel: s('tierWebHide'),
            content: <TierWebCheckoutPreview tierName={tierName} />,
          },
        },
        {
          key: 'reward',
          step: s('showcaseStep', { step: 2 }),
          icon: CardMembershipIcon,
          label: s('tiersRewardLabel'),
          text: s('tiersRewardText'),
          // Upgrading in the app, clickable
          interactive: true,
          itemsBeside: true,
          preview: (
            <TierUpgradeFlow
              current={{ name: tierName }}
              upgrade={{ name: upgradeName }}
            />
          ),
          items: TIER_PRODUCTS.map((product) => ({
            ...product,
            label: s(`tiersProduct-${product.key}`),
          })),
        },
      ]}
      sourcesIcon={DatabaseImportIcon}
      sources={s('tiersInfoSources')}
      collapsible={collapsible}
      action={action}
    />
  );
};

TiersShowcase.propTypes = {
  collapsible: PropTypes.bool,
  action: PropTypes.node,
};

TiersShowcase.defaultProps = {
  collapsible: true,
  action: null,
};

export default TiersShowcase;
